/**
 * HomePageHeader component - A hero image with an overlay containing title, text and CTA button
 * image, title, text, button
 */
import {useEffect, useState} from 'react';
import clsx from 'clsx';
import {useSubdomainBrand} from '~/hooks/useSubdomainBrand';
import {Button} from '~/components/global/Button.client';
import {ResponsiveImageWrapper} from '~/components/global/ResponsiveImageWrapper.client';
import {sanityLinkToAnchor} from '~/utils/link';
import {PromotionAnalytics} from '~/components/PromotionAnalytics.client';

export function HomePageHeader({sanityModule}) {
  const brand = useSubdomainBrand();
  const {
    overlayPosition,
    overlayColor,
    headerHeading,
    headerText,
    image,
    responsiveImage,
    headerHeadingResponsiveStyles,
    headerTextResponsiveStyles,
    buttonGroup,
    promotionTracking,
  } = sanityModule;
  const [currentHeadingStyle, setCurrentHeadingStyle] = useState({});
  const [currentTextStyle, setCurrentTextStyle] = useState({});
  const [overlayStyle, setOverlayStyle] = useState({});
  const windowReady = typeof window !== 'undefined';
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let matchedHeadingStyle = headerHeadingResponsiveStyles?.find((style) => {
        switch (style.breakpoint) {
          case 'mobile':
            return width < 769;
          case 'tablet':
            return width >= 769 && width < 960;
          case 'desktop':
            return width >= 960 && width < 1600;
          case 'desktop-lg':
            return width >= 1600;
          default:
            return false;
        }
      });

      let matchedTextStyle = headerTextResponsiveStyles?.find((style) => {
        switch (style.breakpoint) {
          case 'mobile':
            return width < 769;
          case 'tablet':
            return width >= 769 && width < 960;
          case 'desktop':
            return width >= 960 && width < 1600;
          case 'desktop-lg':
            return width >= 1600;
          default:
            return false;
        }
      });

      setCurrentHeadingStyle(matchedHeadingStyle || {});
      setCurrentTextStyle(matchedTextStyle || {});

      const overlayStyle = {};
      if (overlayColor?.hex) {
        overlayStyle.backgroundColor = overlayColor.hex;
      }
      if (overlayColor?.hex && width >= 769) {
        overlayStyle.opacity = overlayColor.alpha;
      }
      if (!headerHeading?.header && !headerText?.body && !buttonGroup) {
        overlayStyle.display = 'none';
      }
      setOverlayStyle(overlayStyle);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [headerHeadingResponsiveStyles, headerTextResponsiveStyles, windowReady]);
  const headingStyle = {};
  if (currentHeadingStyle?.color?.hex) {
    headingStyle.color = currentHeadingStyle.color.hex;
  }
  if (currentHeadingStyle?.fontSize && currentHeadingStyle?.fontSizeUnit) {
    headingStyle.fontSize = `${currentHeadingStyle?.fontSize}${currentHeadingStyle?.fontSizeUnit}`;
  }
  const textStyle = {};
  if (currentTextStyle?.color?.hex) {
    textStyle.color = currentTextStyle.color.hex;
  }
  if (currentTextStyle?.fontSize && currentTextStyle?.fontSizeUnit) {
    textStyle.fontSize = `${currentTextStyle.fontSize}${currentTextStyle.fontSizeUnit}`;
  }
  // Conditional class and data attribute for promotion tracking
  const headerClassNames = clsx(
    'home-page-header',
    promotionTracking?.enabled && 'promotion-tracking-enabled',
  );

  return (
    <>
      {promotionTracking && (
        <PromotionAnalytics promotionTracking={promotionTracking} />
      )}
      <div
        className={headerClassNames}
        {...(promotionTracking?.enabled
          ? {
              'data-promotion-creative-name':
                promotionTracking?.creativeName ?? 'NA',
            }
          : {})}
      >
        <div className="home-page-header__image-container">
          <ResponsiveImageWrapper
            elementType="img"
            image={image}
            responsiveImage={responsiveImage}
            classNames="home-page-header__image"
          />
        </div>
        <div
          className={clsx(
            'home-page-header__overlays-container',
            'home-page-header__overlays-container__' + overlayPosition,
          )}
        >
          <div className="image-overlay" style={overlayStyle}></div>
          <div className="text-overlay">
            <h2 style={headingStyle}>{headerHeading?.header}</h2>
            <p style={textStyle}>{headerText?.body}</p>
            <div className="btn-group">
              {Array.isArray(buttonGroup) &&
                buttonGroup.map((button) => {
                  const {color, links, buttonText, _key} = button;
                  return (
                    <Button
                      {...sanityLinkToAnchor(links?.[0], brand)}
                      key={_key}
                      ctaText={buttonText}
                      id={_key}
                      variant={color}
                    />
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
